import { MoldOrderStatus, MoldOrder } from '../../types/mold';
import { 
  Section, 
  QuestionFilterStrategy,
  SimpleMoldOrderCondition,
  SimpleOperator 
} from '../../types/form';

/**
 * Simple enhanced filter utilities - streamlined version
 */

/**
 * Gets a value from moldOrder using dot notation path
 */
const getValue = (obj: any, path: string): any => {
  return path.split('.').reduce((current, key) => current?.[key], obj);
};

/**
 * Evaluates a simple condition against moldOrder
 */
const evaluateSimpleCondition = (condition: SimpleMoldOrderCondition, moldOrder: MoldOrder): boolean => {
  const actualValue = getValue(moldOrder, condition.property);
  const expectedValue = condition.value;

  switch (condition.operator) {
    case 'equals':
      return actualValue === expectedValue;
    case 'greaterThan':
      return typeof actualValue === 'number' && actualValue > expectedValue;
    case 'lessThan':
      return typeof actualValue === 'number' && actualValue < expectedValue;
    case 'contains':
      return typeof actualValue === 'string' && actualValue.includes(expectedValue);
    case 'exists':
      return actualValue !== undefined && actualValue !== null;
    case 'arrayContains':
      if (!Array.isArray(actualValue)) return false;
      return actualValue.some((item: any) => 
        typeof expectedValue === 'object' && expectedValue !== null ? 
          Object.keys(expectedValue).every(key => item?.[key] === expectedValue[key]) :
          item === expectedValue
      );
    default:
      return false;
  }
};

/**
 * Simple version of enhanced filtering
 */
export const simpleFilterSections = (
  sections: Section[],
  status: MoldOrderStatus,
  moldOrder: MoldOrder,
  strategy?: QuestionFilterStrategy
): Section[] => {
  if (!strategy) {
    return sections; // No strategy, return all sections
  }

  // Start with status-based filtering (existing logic)
  let statusIncludeTags: string[] = [];
  let statusExcludeTags: string[] = [];
  let statusIncludeAll = false;

  const statusFilter = strategy.statusTagFilters?.[status] || strategy.defaultBehavior;
  if (statusFilter) {
    if (statusFilter.includeAll) {
      statusIncludeAll = true;
    } else {
      statusIncludeTags = statusFilter.includeTags || [];
      statusExcludeTags = statusFilter.excludeTags || [];
    }
  }

  // Evaluate moldOrder rules to determine which tags are allowed
  let moldOrderAllowedTags: Set<string> = new Set();
  let moldOrderBlockedTags: Set<string> = new Set();
  let hasMoldOrderRules = false;

  if (strategy.moldOrderRules && strategy.moldOrderRules.length > 0) {
    hasMoldOrderRules = true;
    
    for (const rule of strategy.moldOrderRules) {
      const conditionMet = evaluateSimpleCondition(rule.condition, moldOrder);
      
      if (conditionMet) {
        // Condition is met - allow these tags
        if (rule.includeTags) {
          rule.includeTags.forEach((tag: string) => moldOrderAllowedTags.add(tag));
        }
      } else {
        // Condition is NOT met - block these tags
        if (rule.includeTags) {
          rule.includeTags.forEach((tag: string) => moldOrderBlockedTags.add(tag));
        }
      }
      
      // Always respect exclude tags regardless of condition
      if (rule.excludeTags) {
        rule.excludeTags.forEach((tag: string) => moldOrderBlockedTags.add(tag));
      }
    }
  }

  // Apply final filter to sections
  return sections
    .map(section => ({
      ...section,
      questions: section.questions.filter(question => {
        const allTags = [...(section.tags || []), ...(question.tags || [])];

        // First check moldOrder blocked tags (highest priority)
        if (hasMoldOrderRules && allTags.some(tag => moldOrderBlockedTags.has(tag))) {
          return false;
        }

        // Check status-based exclude tags
        if (statusExcludeTags.some(tag => allTags.includes(tag))) {
          return false;
        }

        // For tags that have moldOrder rules, they must pass moldOrder conditions
        if (hasMoldOrderRules) {
          const hasRuleControlledTags = allTags.some(tag => 
            [...moldOrderAllowedTags, ...moldOrderBlockedTags].includes(tag)
          );
          
          if (hasRuleControlledTags) {
            // This section/question has tags controlled by moldOrder rules
            // It must have at least one allowed tag and no blocked tags
            const hasAllowedTag = allTags.some(tag => moldOrderAllowedTags.has(tag));
            return hasAllowedTag;
          }
        }

        // If includeAll is set (from status filter or defaultBehavior)
        if (statusIncludeAll) {
          return true;
        }

        // For regular status-based filtering
        if (statusIncludeTags.length > 0) {
          return statusIncludeTags.some(tag => allTags.includes(tag));
        }

        // Default behavior: if no specific rules and no includeAll, 
        // return true for exclusive mode, false for inclusive mode
        return strategy.filterMode !== 'inclusive';
      })
    }))
    .filter(section => section.questions.length > 0);
};

/**
 * Helper functions for creating simple rules
 */
export const createSimpleRule = (
  id: string,
  property: string,
  operator: SimpleOperator,
  value: any,
  includeTags: string[],
  description?: string
) => ({
  id,
  condition: { property, operator, value },
  includeTags,
  description
});

export const createSimpleExcludeRule = (
  id: string,
  property: string,
  operator: SimpleOperator,
  value: any,
  excludeTags: string[],
  description?: string
) => ({
  id,
  condition: { property, operator, value },
  excludeTags,
  description
});

/**
 * Common simple rules
 */
export const SIMPLE_RULES = {
  hotRunner: () => createSimpleRule(
    'hot_runner',
    'mold.hotRunner',
    'equals',
    true,
    ['hot-runner', 'heating-system'],
    'Show hot runner questions for molds with hot runner systems'
  ),

  highPriority: (threshold = 8) => createSimpleRule(
    'high_priority',
    'priority',
    'greaterThan',
    threshold - 1,
    ['urgent-inspection', 'priority-check'],
    `Show priority questions for orders with priority >= ${threshold}`
  ),

  multiCavity: (threshold = 8) => createSimpleRule(
    'multi_cavity',
    'mold.cavity',
    'greaterThan',
    threshold,
    ['multi-cavity', 'complex-mold'],
    `Show multi-cavity questions for molds with more than ${threshold} cavities`
  ),

  heavyMold: (threshold = 1000) => createSimpleRule(
    'heavy_mold',
    'mold.weight',
    'greaterThan',
    threshold,
    ['heavy-mold', 'crane-required'],
    `Show heavy mold procedures for molds over ${threshold}kg`
  ),

  premiumCustomer: () => createSimpleRule(
    'premium_customer',
    'mold.customer.name',
    'contains',
    'Premium',
    ['premium-customer', 'enhanced-qa'],
    'Show enhanced procedures for premium customers'
  ),

  repairOrder: () => createSimpleRule(
    'repair_order',
    'type',
    'contains',
    'repair',
    ['repair-assessment', 'problem-analysis'],
    'Show repair-specific questions for repair orders'
  ),

  // NEW: Rule for PE assessment approval workflow step
  peAssessmentWorkflow: () => createSimpleRule(
    'pe_assessment_workflow',
    'workflowStructure',
    'arrayContains',
    { statusName: 'pe_assessment_approval' },
    ['pe_assessment_approval'],
    'Show PE assessment sections only when pe_assessment_approval exists in workflow'
  )
};

export default {
  simpleFilterSections,
  createSimpleRule,
  createSimpleExcludeRule,
  SIMPLE_RULES
};
